import type { ReactNode } from "react";
import { Table, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import type { Placement } from "react-bootstrap/types";
import { FaEye, FaEdit, FaClock } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  PenFill,
  Search,
  ArrowClockwise,
  SendFill,
  CheckCircleFill,
  ExclamationCircle,
} from "react-bootstrap-icons";
import { formatCnpj, formatDateTimeExtratoList, normalizeText } from "../../../utils";
import { PATHS } from "../../../routes/paths";
import type { PaymentListItem } from "../shared/paymentListTypes";
import { formatSolicitacao, getPaymentActionRule } from "../shared/paymentListUtils";
import "./TabelaPagamento.css";

export interface TabelaPagamentoProps {
  currentItems: PaymentListItem[];
  handleSort: (key: string) => void;
  getSortIcon: (key: string) => string;
  onOpenHistory: (item: PaymentListItem) => void;
  selectedYear?: number;
  formatCurrency: (value: number | null | undefined) => string;
}

const getSituationConfig = (situacaoRaw: string | undefined) => {
  const s = normalizeText(situacaoRaw);

  if (s.includes("assinatura pendente")) {
    return { icon: <PenFill />, className: "status-adequacao-recusa" };
  }

  if (s.includes("programacao pendente") || s.includes("programação pendente")) {
    return { icon: <Search />, className: "status-pendente-analista" };
  }

  if (s.includes("em aprovacao no fusion") || s.includes("em aprovação no fusion")) {
    return { icon: <ArrowClockwise />, className: "status-andamento" };
  }

  if (s.includes("pagamento provisionado")) {
    return { icon: <SendFill />, className: "status-enviado" };
  }

  if (s.includes("pagamento concluido") || s.includes("pagamento concluído")) {
    return { icon: <CheckCircleFill color="#067647" />, className: "status-plain" };
  }

  return { icon: <ExclamationCircle />, className: "status-default" };
};

const TabelaPagamento = ({
  currentItems,
  handleSort,
  getSortIcon,
  onOpenHistory,
  selectedYear,
  formatCurrency,
}: TabelaPagamentoProps) => {
  const navigate = useNavigate();

  const renderTooltip = (text: string) => (
    <Tooltip id={`tooltip-${String(text || "").replace(/\s+/g, "-")}`}>
      {text}
    </Tooltip>
  );

  const withTooltip = (
    text: string,
    children: ReactNode,
    placement: Placement = "top",
  ) => {
    const title = String(text ?? "").trim();
    if (!title) return children;

    return (
      <OverlayTrigger
        placement={placement}
        delay={{ show: 250, hide: 120 }}
        overlay={renderTooltip(title)}
      >
        <span className="tooltip-target">{children}</span>
      </OverlayTrigger>
    );
  };

  const openDetail = (item: PaymentListItem, readOnly: boolean) => {
    const idSolicitacao = item?.idSolicitacao;
    if (!idSolicitacao) return;

    navigate(PATHS.PAYMENT_PAGAMENTO.replace(":idSolicitacao", String(idSolicitacao)), {
      state: {
        readOnly,
        selectedYear: selectedYear ?? item.ano,
        situacao: item.situacaoPagamento,
        solicitacao: item.solicitacao,
      },
    });
  };

  return (
    <div className="table-responsive-wrapper">
      <Table bordered hover className="payment-table">
        <thead>
          <tr>
            <th>{withTooltip("CNPJ", "CNPJ")}</th>

            <th onClick={() => handleSort("sigla")} className="sortable-header">
              {withTooltip("Sigla", <>Sigla {getSortIcon("sigla")}</>)}
            </th>

            <th
              onClick={() => handleSort("situacaoPagamento")}
              className="sortable-header"
            >
              {withTooltip(
                "Situação",
                <>Situação {getSortIcon("situacaoPagamento")}</>,
              )}
            </th>

            <th
              onClick={() => handleSort("solicitacao")}
              className="sortable-header"
            >
              {withTooltip(
                "Solicitação",
                <>Solicitação {getSortIcon("solicitacao")}</>,
              )}
            </th>

            <th
              onClick={() => handleSort("assinatura")}
              className="sortable-header"
            >
              {withTooltip(
                "Assinatura Formulário",
                <>Assinatura Formulário {getSortIcon("assinatura")}</>,
              )}
            </th>

            <th
              onClick={() => handleSort("dataEmissao")}
              className="sortable-header"
            >
              {withTooltip(
                "Data Emissão",
                <>Data Emissão {getSortIcon("dataEmissao")}</>,
              )}
            </th>

            <th
              onClick={() => handleSort("valorBruto")}
              className="sortable-header"
            >
              {withTooltip(
                "Valor Bruto",
                <>Valor Bruto {getSortIcon("valorBruto")}</>,
              )}
            </th>

            <th className="text-center">{withTooltip("Ações", "Ações")}</th>
          </tr>
        </thead>

        <tbody>
          {currentItems.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center py-4 text-muted">
                Nenhum sindicato encontrado.
              </td>
            </tr>
          ) : (
            currentItems.map((item, index) => {
              const rule = getPaymentActionRule(item);
              const situacaoLabel = item?.situacaoPagamento || "-";
              const { icon, className } = getSituationConfig(
                item?.situacaoPagamento,
              );
              const assinaturaText = item?.dataAssinaturaSolicitacao
                ? formatDateTimeExtratoList(item.dataAssinaturaSolicitacao)
                : "-";
              const emissaoText = item?.dataEmissao
                ? formatDateTimeExtratoList(item.dataEmissao)
                : "-";
              const valorText =
                item?.valorBruto == null ? "-" : formatCurrency(item.valorBruto);

              return (
                <tr key={item.idSolicitacao ?? index}>
                  <td>
                    {withTooltip(
                      formatCnpj(item.cnpj),
                      formatCnpj(item.cnpj) || "-",
                    )}
                  </td>

                  <td>
                    {withTooltip(
                      item.nomeSindicato || item.sigla || "",
                      item.sigla || "-",
                    )}
                  </td>

                  <td>
                    {withTooltip(
                      situacaoLabel,
                      <div className="situation-cell">
                        <span className={`situation-icon ${className}`}>
                          {icon}
                        </span>
                        <span className="situation-text">{situacaoLabel}</span>
                      </div>,
                    )}
                  </td>

                  <td>{formatSolicitacao(item.solicitacao)}</td>
                  <td>{assinaturaText}</td>
                  <td>{emissaoText}</td>
                  <td>{valorText}</td>

                  <td>
                    <div className="action-buttons d-flex gap-2 justify-content-center">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`edit-${item.idSolicitacao}`}>
                            {rule.canProgram ? rule.programReason : rule.viewReason}
                          </Tooltip>
                        }
                      >
                        <span className="d-inline-block">
                          <Button
                            className="btn-edit"
                            size="sm"
                            disabled={!rule.canProgram && !rule.canView}
                            onClick={() => openDetail(item, !rule.canProgram)}
                            aria-label={
                              rule.canProgram ? "Realizar programação" : "Visualizar"
                            }
                            title={
                              rule.canProgram ? "Realizar programação" : "Visualizar"
                            }
                            style={
                              !rule.canProgram && !rule.canView
                                ? { opacity: 0.2, cursor: "not-allowed" }
                                : {}
                            }
                          >
                            {rule.canProgram ? <FaEdit aria-hidden /> : <FaEye aria-hidden />}
                          </Button>
                        </span>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`history-${item.idSolicitacao}`}>
                            Ver histórico
                          </Tooltip>
                        }
                      >
                        <span className="d-inline-block">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onOpenHistory(item)}
                            aria-label="Ver histórico"
                            title="Ver histórico"
                          >
                            <FaClock />
                          </Button>
                        </span>
                      </OverlayTrigger>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default TabelaPagamento;
